import { useUserValuesQuery } from '@dieterApi/persistentState/useUserValuesQuery';

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useQuestionaireRoute } from './useQuestionaireRoute';
import { useUserWithCompany } from './useUserWithCompany';

/**
 * Hook to access user values (PersistentState data) for the current context.
 * Handles both authenticated users and token-based access.
 *
 * For token-based access, this hook automatically fetches the company's
 * user values via the getUserValues query instead of embedding
 * them in the JWT token.
 */
export function useUserValuesData() {
  const { user } = useUserWithCompany();
  const { jwt } = useQuestionaireRoute();
  const [getUserValues, { data, loading, error }] = useUserValuesQuery();
  const lastFetchedKey = useRef<string | null>(null);

  // Fetch user values - with token if present, otherwise for authenticated user
  useEffect(() => {
    // Create a unique key for the current fetch context
    const currentKey = jwt || user?.company?.id || null;

    // Only fetch if the context has changed
    if (lastFetchedKey.current === currentKey) return;

    if (jwt) {
      // Token-based access
      getUserValues({ variables: { token: jwt } });
      lastFetchedKey.current = jwt;
    } else if (user?.company?.id && !jwt) {
      // Authenticated user access (no token in variables)
      getUserValues({ variables: { companyId: user.company.id } });
      lastFetchedKey.current = user.company.id;
    }
  }, [jwt, user?.company?.id]);

  const userValues = data?.getUserValues;

  // Memoized function to get a user value by key and optional index
  const getValue = useCallback(
    (key: string, index?: string) => {
      if (!userValues) return undefined;
      const targetIndex = index || 'UNIQUE';
      const userValue = userValues.find((uv) => uv.key === key && uv.index === targetIndex);
      return userValue?.value;
    },
    [userValues]
  );

  // Memoize the return object to prevent unnecessary re-renders
  return useMemo(
    () => ({
      userValues,
      userValuesLoading: loading,
      error,
      getValue,
    }),
    [userValues, loading, error, getValue]
  );
}
